"use client";

import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Grid } from "@astryxdesign/core/Grid";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";

import { LandingSectionBg } from "@/components/landing/landing-section-bg";
import { LandingDeviceMockup } from "@/components/landing/landing-device-mockup";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { useLeadRequest } from "@/components/landing/lead-request-context";
import { LANDING_PROSE_MAX, LANDING_SECTION_MAX } from "@/lib/landing/layout";
import {
  landingTextProps,
  styledTextString,
  styledTextStyle,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingEnterpriseCtaViewProps = {
  sectionId?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  imageUrl?: string;
  imageUrlDark?: string;
  primaryLabel?: LandingTextValue;
  primaryHref?: string;
  /** Ключ CRM-кейса; если задан — primary открывает модалку заявки. */
  primaryLeadFormKey?: string;
  secondaryLabel?: LandingTextValue;
  secondaryHref: string;
  backgroundImageUrl?: string;
  backgroundColor?: string;
};

/** CTA для акселераторов: слева шапка + текст + кнопки, справа device-мокап. */
export function LandingEnterpriseCtaView({
  sectionId = "enterprise",
  title = "Для акселераторов и команд",
  subtitle = "White-label, объёмные пакеты и единый формат отчётов для резидентов или продуктовой линейки. Обсудим пилот под вашу когорту.",
  imageUrl = "",
  imageUrlDark = "",
  primaryLabel = "Обсудить пилот",
  primaryHref = "/register",
  primaryLeadFormKey = "discuss-pilot",
  secondaryLabel = "Сначала проверить идею",
  secondaryHref,
  backgroundImageUrl = "",
  backgroundColor,
}: LandingEnterpriseCtaViewProps) {
  const { openLeadRequest } = useLeadRequest();
  const useLeadModal = Boolean(primaryLeadFormKey?.trim());
  const primaryText = styledTextString(primaryLabel);
  const secondaryText = styledTextString(secondaryLabel);

  return (
    <LandingSectionBg backgroundImageUrl={backgroundImageUrl} backgroundColor={backgroundColor}>
      <Section id={sectionId} variant="transparent" paddingBlock={10}>
        <Center>
          <div className="mx-auto w-full px-6" style={{ maxWidth: LANDING_SECTION_MAX }}>
            <Grid
              columns={{ minWidth: 280, max: 2 }}
              gap={10}
              width="100%"
              className="items-center"
            >
              <VStack
                gap={5}
                align="start"
                maxWidth={LANDING_PROSE_MAX}
                width="100%"
                className="min-h-0 self-stretch sm:min-h-[320px]"
              >
                <VStack gap={4} align="start" width="100%">
                  <LandingCanvasText field="title" value={title} label="Заголовок" multiline>
                    <Heading level={2} type="display-3" {...landingTextProps(title)} />
                  </LandingCanvasText>
                  <LandingCanvasText
                    field="subtitle"
                    value={subtitle}
                    label="Подзаголовок"
                    multiline
                    hideIfEmpty
                  >
                    <Text type="large" color="secondary" {...landingTextProps(subtitle)} />
                  </LandingCanvasText>
                </VStack>

                <HStack gap={3} wrap="wrap" className="mt-auto pt-2">
                  <LandingCanvasText
                    field="primaryLabel"
                    value={primaryLabel}
                    label="Основная кнопка"
                    inline
                  >
                    {useLeadModal ? (
                      <Button
                        label={primaryText}
                        variant="primary"
                        size="lg"
                        style={styledTextStyle(primaryLabel)}
                        onClick={() =>
                          openLeadRequest({
                            formCaseKey: primaryLeadFormKey!.trim(),
                            title: primaryText,
                            subtitle:
                              "Оставьте контакты — обсудим пилот под вашу когорту.",
                          })
                        }
                      />
                    ) : (
                      <a href={primaryHref}>
                        <Button
                          label={primaryText}
                          variant="primary"
                          size="lg"
                          style={styledTextStyle(primaryLabel)}
                        />
                      </a>
                    )}
                  </LandingCanvasText>
                  <LandingCanvasText
                    field="secondaryLabel"
                    value={secondaryLabel ?? ""}
                    label="Вторая кнопка"
                    inline
                    hideIfEmpty
                  >
                    <a href={secondaryHref}>
                      <Button
                        label={secondaryText}
                        variant="secondary"
                        size="lg"
                        style={styledTextStyle(secondaryLabel)}
                      />
                    </a>
                  </LandingCanvasText>
                </HStack>
              </VStack>

              <div className="w-full min-w-0">
                <LandingDeviceMockup
                  imageUrlLight={imageUrl}
                  imageUrlDark={imageUrlDark}
                  alt={styledTextString(title)}
                />
              </div>
            </Grid>
          </div>
        </Center>
      </Section>
    </LandingSectionBg>
  );
}
